import { useState } from "react";
import "./OrderPricingSummary.css";
import {
  ClipboardList,
  ChevronUp,
  ChevronDown,
  Info,
  Check,
  AlertCircle,
  X,
} from "lucide-react";

const OrderPricingSummary = ({ pricing }) => {
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  if (!pricing) return null;

  const canonicalDiscounts = pricing.canonical_discounts;
  const discountSources = canonicalDiscounts?.sources || [];
  const coverage = canonicalDiscounts?.coverage;

  const hasDiscounts = pricing.discount_total > 0;
  const adjustmentBreakdown = pricing.pricing_adjustment_breakdown;
  const profitResult = pricing.profit_result; 


const label = pricing.canonical_discounts?.label;

const badgeConfig = {
  "All discounts applied": {
    className: "ops-badge ops-badge--success",
    icon: <Check size={13} />,
  },
  "Some discounts were not applied": {
    className: "ops-badge ops-badge--warning",
    icon: <AlertCircle size={13} />,
  },
  "No discounts applied": {
    className: "ops-badge ops-badge--warning",
    icon: <AlertCircle size={13} />,
  },
}[label] || {
  className: "ops-badge ops-badge--success",
  icon: <Check size={13} />,
};


  return (
    <>
      <div className="ops-card mb-3">
        {/* Header */}
        <div
          className="ops-header"
          onClick={() => setIsOpen((prev) => !prev)}
          style={{ cursor: "pointer" }}
        >
          <span className="ops-title">
            <ClipboardList size={18} />
            Order Summary
          </span>
          {isOpen ? (
            <ChevronUp size={18} className="ops-chevron" />
          ) : (
            <ChevronDown size={18} className="ops-chevron" />
          )}
        </div>

        {isOpen && (
          <div className="p-2 mt-2 border-top border-1">
            {/* Subtotal */}
            <div className="ops-row">
              <span className="ops-label">
                Sub Total (
                {pricing.totals?.subtotal?.label?.match(/\d+/)?.[0] || 1}{" "}
                services ) :
              </span>
              <span className="ops-value">
                {Number(pricing.subtotal).toLocaleString()} EGP
              </span>{" "}
            </div>

            {/* Discounts */}
            {hasDiscounts && (
              <>
                <div className="ops-row ops-row--align-start">
                  <div className="ops-discount-left">
                    <span className="ops-label">Discounts:</span>
                    <span className={badgeConfig.className}>
                      {badgeConfig.icon}
                      {label}
                    </span>
                  </div>
                  <button
                    className="ops-info-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDiscountModalOpen(true);
                    }}
                    aria-label="Discount details"
                  >
                    <Info size={16} />
                  </button>
                </div>

                <div className="ops-discount-chips">
                  {discountSources.map((discount, idx) => (
                    <div className="ops-chip" key={idx}>
                      <i className="ti ti-tag" aria-hidden="true" />
                      <span className="ops-chip-label">{discount.label}:</span>
                      <span className="ops-chip-val">
                        -{discount.formatted?.amount} EGP
                      </span>
                    </div>
                  ))}
                  <span className="ops-discount-total-val ms-auto">
                    -{canonicalDiscounts?.formatted_total_discount} EGP
                  </span>
                </div>

                {/* Covered by */}
                {coverage && (
                  <div className="ops-covered">
                    Discount covered by:{" "}
                    {coverage.estbn_borne?.amount > 0 &&
                      `ESTBN ${coverage.estbn_borne.formatted} EGP  `}
                    {coverage.provider_borne?.amount > 0 &&
                      `Provider ${coverage.provider_borne.formatted} EGP  `}
                    {coverage.unknown_borne?.amount > 0 &&
                      `Unknown ${coverage.unknown_borne.formatted} EGP`}
                  </div>
                )}
              </>
            )}
            {/* Pricing Adjustments */}
            {adjustmentBreakdown?.items?.length > 0 && (
              <>
                <div className="ops-section-label">Pricing Adjustments:</div>
                <div className="ops-inline-items">
                  {adjustmentBreakdown.items.map((item, idx) => (
                    <div className="ops-inline-chip" key={idx}>
                      <i
                        className={`ti ${item.type === "surcharge" ? "ti-arrow-up" : "ti-arrow-down"}`}
                        aria-hidden="true"
                      />
                      <span className="ops-chip-label">{item.rule_name}:</span>
                      <span
                        className="ops-value"
                        style={{
                          color:
                            item.type === "discount" ? "#e57373" : "#3EADA3",
                        }}
                      >
                        {item.type === "discount" ? "" : "+"}
                        {item.amount} EGP
                        {item.cap_applied && (
                          <span
                            className="ops-note"
                            style={{ fontSize: "10px", marginLeft: "4px" }}
                          >
                            (capped from {item.uncapped_amount} EGP)
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="ops-row" style={{ marginTop: "4px" }}>
                  <span
                    className="ops-label"
                    style={{ color: "#888", fontSize: "11px" }}
                  >
                    Net Adjustment:
                  </span>
                  <span
                    className="ops-value"
                    style={{
                      color:
                        adjustmentBreakdown.net_total === 0
                          ? "#888"
                          : adjustmentBreakdown.net_total < 0
                            ? "#e57373"
                            : "#3EADA3",
                      fontWeight: 600,
                    }}
                  >
                    {adjustmentBreakdown.formatted_net_total} EGP
                  </span>
                </div>
              </>
            )}
            {/* Fees */}
            <div className="ops-section-label">Fees Summary:</div>
            <div className="ops-inline-items">
              {pricing.fees?.items?.length > 0 ? (
                <div className=" d-flex justify-content-between align-items-center w-100">
                  {pricing.fees.items.map((fee, idx) => (
                    <div className="ops-inline-chip" key={idx}>
                      <i className="ti ti-tool" aria-hidden="true" />
                      <span className="ops-chip-label">{fee.label}:</span>
                      <span className="ops-value">{fee.formatted} EGP</span>
                    </div>
                  ))}
                  <span className="ops-section-total">
                    {pricing.fees.formatted_total} EGP
                  </span>
                </div>
              ) : (
                <div className="ops-inline-chip">
                  <i className="ti ti-truck-delivery" aria-hidden="true" />
                  <span className="ops-chip-label">Delivery Fee:</span>
                  <span className="ops-value">{pricing.delivery_fee} EGP</span>
                </div>
              )}
            </div>
            <div className="ops-note">{pricing.fees?.note}</div>
            {/* Taxes */}
            {pricing.taxes?.items?.length > 0 && (
              <>
                <div className="ops-section-label">Taxes:</div>
                <div className="ops-inline-items d-flex justify-content-between align-items-center">
                  {pricing.taxes.items.map((tax, idx) => (
                    <div className="ops-inline-chip" key={idx}>
                      <i className="ti ti-receipt-tax" aria-hidden="true" />
                      <span className="ops-chip-label">{tax.label}:</span>
                      <span className="ops-value">{tax.formatted} EGP</span>
                    </div>
                  ))}
                  <span className="ops-section-total">
                    {pricing.taxes.formatted_total} EGP
                  </span>
                </div>
                <div className="ops-note">{pricing.taxes.note}</div>
              </>
            )}

            <hr className="ops-divider" />

            {/* Total */}
            <div className="ops-total-row">
              <span className="ops-total-label">Total:</span>
              <span className="ops-total-value">
                {pricing.customer_payable_total?.toLocaleString()} EGP
              </span>
            </div>

            {/* Profit Result */}
            {profitResult && (
              <>
                <div
                  className="ops-section-label"
                  style={{ marginTop: "10px" }}
                >
                  Profit Breakdown:
                </div>
                <div className="ops-revenue-row">
                  {[
                    {
                      label: "Gross Revenue (ex. VAT)",
                      value: profitResult.gross_revenue_ex_vat,
                    },
                    {
                      label: "Net Revenue (ex. VAT)",
                      value: profitResult.net_revenue_ex_vat,
                    },
                    { label: "Gross Profit", value: profitResult.gross_profit },
                    {
                      label: "Gross Margin",
                      value: null,
                      pct: profitResult.gross_margin_percentage,
                    },
                    {
                      label: "Est. Corporate Tax",
                      value: profitResult.estimated_corporate_tax,
                      negative: true,
                    },
                    {
                      label: "Net Profit (after tax)",
                      value: profitResult.net_profit_after_tax,
                    },
                    {
                      label: "Net Margin (after tax)",
                      value: null,
                      pct: profitResult.net_margin_after_tax_percentage,
                    },
                  ].map((item, idx) => (
                    <div className="ops-revenue-chip" key={idx}>
                      <i
                        className={`ti ${item.negative ? "ti-receipt-tax" : "ti-chart-bar"}`}
                        aria-hidden="true"
                      />
                      <span className="ops-revenue-chip-label">
                        {item.label}:
                      </span>
                      <span
                        className="ops-revenue-chip-val"
                        style={{
                          color: item.negative
                            ? "#e57373"
                            : item.pct
                              ? "#888"
                              : "#3EADA3",
                        }}
                      >
                        {item.pct != null
                          ? `${item.pct}%`
                          : `${item.negative ? "-" : ""}${item.value?.toLocaleString()} EGP`}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Discount Modal */}
      {discountModalOpen && (
        <div
          className="ops-modal-overlay"
          onClick={() => setDiscountModalOpen(false)}
        >
          <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="ops-modal-header">
              <span className="ops-modal-title mb-0">
                <i className="ti ti-tag" aria-hidden="true" />
                {canonicalDiscounts?.title || "Discount Breakdown"}
              </span>
              <button
                className="ops-modal-close"
                onClick={() => setDiscountModalOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Status */}
            <div className="ops-modal-status mb-2">
              <p className="my-0 text-muted small">
                Total Discount:{" "}
                <span className="fw-bold">
                  {canonicalDiscounts?.formatted_total_discount} EGP
                </span>
              </p>
              <span className={badgeConfig.className}>
                {badgeConfig.icon}
                {badgeConfig.label}
              </span>
            </div>

            {/* Discount Sources Table */}
            <div className="ops-modal-table-wrap">
              <table className="ops-modal-table">
                <thead>
                  <tr>
                    <th>Discount</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Paid By</th>
                  </tr>
                </thead>
                <tbody>
                  {discountSources.map((discount, idx) => (
                    <tr key={idx}>
                      <td>{discount.label}</td>
                      <td>{discount.source_type}</td>
                      <td className="ops-discount-total-val">
                        -{discount.formatted?.amount} EGP
                      </td>
                      <td>
                        <span
                          className={`ops-chip ops-chip--sm ${
                            discount.status === "applied"
                              ? "ops-chip--success"
                              : "ops-chip--warning"
                          }`}
                        >
                          {discount.status}
                        </span>
                      </td>
                      <td>{discount.who_pays || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="ops-modal-footer">
              {/* Covered by */}
              {coverage && (
                <div className="ops-modal-covered">
                  <span className="ops-label">Covered by:</span>
                  <div className="ops-covered-chips">
                    {coverage.estbn_borne?.amount > 0 && (
                      <span className="ops-chip">
                        ESTBN: {coverage.estbn_borne.formatted} EGP
                      </span>
                    )}
                    {coverage.provider_borne?.amount > 0 && (
                      <span className="ops-chip">
                        Provider: {coverage.provider_borne.formatted} EGP
                      </span>
                    )}
                    {coverage.unknown_borne?.amount > 0 && (
                      <span className="ops-chip">
                        Unknown: {coverage.unknown_borne.formatted} EGP
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Reconciliation note */}
              {canonicalDiscounts?.reconciliation?.difference !== 0 && (
                <div className="ops-note ops-note--warning">
                  ⚠️ {canonicalDiscounts?.reconciliation?.note}
                </div>
              )}

              <div className="ops-modal-total-row">
                <span className="ops-label">Total Discount:</span>
                <span className="ops-discount-total-val ops-value">
                  -{canonicalDiscounts?.formatted_total_discount} EGP
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderPricingSummary;
